class HolInfiniteProducts extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.dataset.initialized = 'true';
    this.loading = false;
    this.retryCount = 0;

    if (!this.dataset.nextUrl) {
      this.remove();
      return;
    }

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
        },
        { rootMargin: '800px 0px' },
      );
      this.observer.observe(this);
      return;
    }

    this.loadNextPage();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.controller?.abort();
  }

  async loadNextPage() {
    if (this.loading || !this.dataset.nextUrl) return;

    this.loading = true;
    this.setAttribute('aria-busy', 'true');
    const status = this.querySelector('[data-infinite-status]');
    if (status) status.textContent = 'Loading more products';

    try {
      this.controller = new AbortController();
      const url = new URL(this.dataset.nextUrl, window.location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);

      const response = await fetch(url.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: this.controller.signal,
      });
      if (!response.ok) throw new Error(`Unable to load products (${response.status})`);

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const listSelector = this.dataset.layout === 'rows' ? '#product-rows' : '#product-grid';
      const currentList = document.querySelector(listSelector);
      const nextList = documentFragment.querySelector(listSelector);
      if (!currentList || !nextList) throw new Error('Product list was not found');

      const newProducts = document.createDocumentFragment();
      [...nextList.children].forEach((item) => {
        if (item.matches('.grid-item-block, .product-row-block')) return;
        newProducts.appendChild(item);
      });
      currentList.appendChild(newProducts);

      const nextLoader = documentFragment.querySelector('hol-infinite-products');
      if (!nextLoader?.dataset.nextUrl) {
        this.remove();
      } else {
        this.dataset.nextUrl = nextLoader.dataset.nextUrl;
        this.retryCount = 0;
        this.observer?.unobserve(this);
        requestAnimationFrame(() => this.observer?.observe(this));
      }

      window.jdgm?.init_widgets?.();
      document.dispatchEvent(
        new CustomEvent('hol:products:loaded', {
          detail: { sectionId: this.dataset.sectionId },
        }),
      );
    } catch (error) {
      if (error.name !== 'AbortError' && this.retryCount < 2) {
        this.retryCount += 1;
        window.setTimeout(() => this.loadNextPage(), 1500 * this.retryCount);
      }
    } finally {
      this.loading = false;
      this.removeAttribute('aria-busy');
      if (status) status.textContent = '';
    }
  }
}

if (!customElements.get('hol-infinite-products')) {
  customElements.define('hol-infinite-products', HolInfiniteProducts);
}
